// src/pages/ZeroRetention.jsx
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

export default function ZeroRetention() {
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    return (
        <div className="min-h-screen flex flex-col bg-[#07090e] text-slate-300 antialiased relative overflow-x-hidden">
            {/* Ambient Background Radials for High Aesthetic Depth */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[350px] bg-gradient-to-b from-indigo-500/[0.03] to-transparent blur-[120px] pointer-events-none rounded-full" />
            <div className="absolute top-[40%] right-[-10%] w-[400px] h-[400px] bg-amber-500/[0.01] blur-[150px] pointer-events-none rounded-full" />

            <Navbar />

            {/* Core Consumer-Facing Container */}
            <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-16 lg:py-24 relative z-10 flex flex-col justify-center">

                <div className={`w-full transition-all duration-1000 ease-out transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
                    }`}>

                    {/* Centered Editorial Header */}
                    <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
                        <div className="inline-flex items-center gap-2.5 bg-emerald-400/[0.06] border border-emerald-400/25 rounded-full px-5 py-2 mb-5 backdrop-blur-sm">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />
                            <span className="text-sm font-sans font-semibold tracking-widest text-emerald-400 uppercase">
                                Architecture Verified
                            </span>
                        </div>
                        <h1 className="font-sans font-bold text-3xl sm:text-4xl text-white tracking-tight mb-3">
                            Zero Retention Policy
                        </h1>
                        <p className="text-sm font-sans font-light text-slate-400 tracking-wide">
                            Last Updated: June 2026 &bull; Version 1.0
                        </p>
                    </div>

                    {/* Main Content Card */}
                    <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-10 shadow-2xl backdrop-blur-xl relative">

                        {/* Introduction */}
                        <div className="mb-8">
                            <p className="text-base font-body font-light text-slate-300 leading-[1.85]">
                                Guardian Message Check ("GMC") has been built with privacy and confidentiality at its core.
                            </p>
                            <p className="text-base font-body font-light text-slate-300 leading-[1.85] mt-4">
                                Our Zero Retention Policy explains how communications submitted to the platform are processed and our commitment to minimising the storage of user data.
                            </p>
                            <p className="text-base font-body font-light text-slate-300 leading-[1.85] mt-4">
                                This policy applies to all Guardian Message Check users and should be read alongside our Terms and Conditions, Privacy Policy and other applicable platform policies.
                            </p>
                        </div>

                        <div className="space-y-12 text-base font-body font-light text-slate-300 leading-[1.85]">

                            {/* Section 1 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    1. Our Commitment to Zero Retention
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check is designed to process communications without intentionally retaining the content of those communications after processing has been completed.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Zero Retention is a core design principle of the platform and applies across all Guardian Message Check plans, including:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>Free Accounts</li>
                                    <li>Pay-As-You-Go (PAYG)</li>
                                    <li>Individual Subscription Plans</li>
                                    <li>Business Accounts</li>
                                    <li>Enterprise Accounts</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Our goal is to ensure that users can analyse sensitive communications with confidence, knowing that submitted message content is not intentionally stored once the requested analysis has been completed.
                                </p>
                            </div>

                            {/* Section 2 */}
                            <div className="border-l-2 border-emerald-500/40 pl-4 py-1">
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    2. What Zero Retention Means
                                </h2>
                                <p className="text-slate-400">
                                    When you submit a communication to Guardian Message Check, the content is processed solely for the purpose of providing the services you have requested.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    These services may include:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>communication risk analysis;</li>
                                    <li>plain-English explanations;</li>
                                    <li>communication insights;</li>
                                    <li>professional rewrite suggestions.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Once processing has been completed, Guardian Message Check does not intentionally retain or archive the body text of the submitted communication.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Guardian Message Check does not build databases of submitted communications.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Guardian Message Check does not use submitted communications to train proprietary artificial intelligence models.
                                </p>
                            </div>

                            {/* Section 3 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    3. What We Do Store
                                </h2>
                                <p className="text-slate-400">
                                    To operate the platform securely, Guardian Message Check stores limited account-related information necessary for the operation of your account.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    This may include:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>your account details;</li>
                                    <li>your email address;</li>
                                    <li>your subscription status;</li>
                                    <li>billing information;</li>
                                    <li>platform preferences;</li>
                                    <li>authentication information.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    This information is necessary to provide the Guardian Message Check service and does not include the body text of communications submitted for analysis.
                                </p>
                            </div>

                            {/* Section 4 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    4. Local Storage
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check does not use advertising or tracking cookies.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Instead, the platform uses limited browser Local Storage for essential functionality only.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Local Storage is used solely to:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>keep you securely signed in;</li>
                                    <li>remember limited account information required for the dashboard;</li>
                                    <li>remember your interface preferences;</li>
                                    <li>remember onboarding completion.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    This information remains within your own browser and is not used for advertising, behavioural profiling or cross-site tracking.
                                </p>
                            </div>

                            {/* Section 5 */}
                            <div className="border-l-2 border-emerald-500/40 pl-4 py-1">
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    5. Security During Processing
                                </h2>
                                <p className="text-slate-400">
                                    Although communications are not intentionally retained after processing, they must be processed temporarily in order to generate the requested analysis.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Guardian Message Check uses reasonable technical and organisational measures to protect information while it is being processed.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Temporary processing is limited to what is reasonably necessary to provide the requested service, maintain platform security, prevent abuse and comply with applicable legal obligations.
                                </p>
                            </div>

                            {/* Section 6 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    6. What Zero Retention Does Not Mean
                                </h2>
                                <p className="text-slate-400">
                                    Zero Retention does not mean that Guardian Message Check stores no information whatsoever.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    To operate the platform securely and provide user accounts, Guardian Message Check necessarily stores limited operational information such as account details, subscription status, billing records and authentication information.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Zero Retention applies specifically to the content of communications submitted for analysis.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    It does not prevent Guardian Message Check from retaining information where retention is required by law, necessary for platform security, fraud prevention, account management or other legitimate operational purposes.
                                </p>
                            </div>

                            {/* Section 7 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    7. User Responsibilities
                                </h2>
                                <p className="text-slate-400">
                                    Users remain responsible for ensuring that:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>they have the legal right to submit any communication for analysis;</li>
                                    <li>submitted content does not breach applicable laws or the rights of third parties;</li>
                                    <li>they review all AI-generated analysis and rewrite suggestions before relying upon or sending them;</li>
                                    <li>they protect their own account credentials and devices.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Guardian Message Check provides AI-assisted communication analysis only. Users remain solely responsible for all decisions and actions taken using the platform.
                                </p>
                            </div>

                            {/* Section 8 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    8. Legal Exceptions
                                </h2>
                                <p className="text-slate-400">
                                    Although Guardian Message Check is committed to Zero Retention, there may be limited circumstances where information is processed or retained where required by law.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    These circumstances may include:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>compliance with lawful court orders;</li>
                                    <li>compliance with statutory or regulatory obligations;</li>
                                    <li>investigation of fraud, abuse or criminal activity;</li>
                                    <li>protection of Guardian Message Check, its users or the public;</li>
                                    <li>maintaining the security and integrity of the platform.</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    Where such circumstances arise, Guardian Message Check will process only the minimum information reasonably necessary to comply with its legal obligations.
                                </p>
                            </div>

                            {/* Section 9 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    9. Changes to This Policy
                                </h2>
                                <p className="text-slate-400">
                                    Guardian Message Check may update this Zero Retention Policy from time to time to reflect changes in technology, legislation, platform functionality or business operations.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    The latest version of this policy will always be published on the Guardian Message Check website.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    Continued use of the platform after any updates take effect constitutes acceptance of the revised policy.
                                </p>
                            </div>

                            {/* Section 10 */}
                            <div>
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    10. Contact Information
                                </h2>
                                <p className="text-slate-400">
                                    If you have any questions regarding this Zero Retention Policy or how Guardian Message Check processes communications, please contact:
                                </p>
                                <div className="mt-3 p-4 bg-white/[0.02] border border-white/[0.04] rounded-xl">
                                    <p className="text-slate-300 font-medium">Guardian Message Check</p>
                                    <p className="text-slate-400 mt-2">Email: <a href="mailto:admin@guardianmessagecheck.com" className="mail-link">admin@guardianmessagecheck.com</a></p>
                                    <p className="text-slate-400">Website: guardianmessagecheck.com</p>
                                </div>
                                <p className="text-slate-400 mt-4">
                                    We aim to respond to genuine enquiries as promptly as reasonably practicable.
                                </p>
                            </div>

                            {/* Section 11 */}
                            <div className="bg-gradient-to-r from-emerald-500/[0.06] to-transparent border border-emerald-500/10 rounded-xl p-4">
                                <h2 className="text-xl font-sans font-semibold text-white mb-4 tracking-wide">
                                    11. Final Provisions
                                </h2>
                                <p className="text-slate-400">
                                    This Zero Retention Policy should be read together with the following Guardian Message Check documents:
                                </p>
                                <ul className="list-disc list-inside text-slate-400 space-y-2 mt-3 ml-2">
                                    <li>Terms and Conditions</li>
                                    <li>Privacy Policy</li>
                                    <li>Cookies & Browser Storage Policy</li>
                                    <li>Disclaimer</li>
                                </ul>
                                <p className="text-slate-400 mt-4">
                                    If any provision of this policy is found to be unlawful, invalid or unenforceable, the remaining provisions shall continue in full force and effect.
                                </p>
                                <p className="text-slate-400 mt-4">
                                    This Zero Retention Policy forms part of the overall legal framework governing the use of the Guardian Message Check platform.
                                </p>
                            </div>

                        </div>

                        {/* Back-Navigation Trigger & Footer Actions */}
                        <div className="mt-12 pt-6 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-4">
                            <Link to="/register" className="back-link group">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="back-arrow">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                <span>Return to Account Creation</span>
                            </Link>

                            <a href="mailto:admin@guardianmessagecheck.com" className="mail-link text-sm font-sans tracking-wide">
                                admin@guardianmessagecheck.com
                            </a>
                        </div>

                    </div>

                </div>
            </main>

            <Footer />
        </div>
    )
}